import React, { useContext, useEffect, useCallback, useRef, useMemo, useState } from 'react';
import Loader from '../../components/Loader';
import WordExplain from '../../components/WordExplain';
import SearchInput from '../../components/SearchInput';
import Slider from '../../components/Slider';
import Href from '../../components/Href';
import Button, { buttonType } from '../../components/Button';
import OuterLayer from '../../containers/OuterLayer';
import SearchResults from './searchResults';
import FullAnalyzeData from './fullAnalyzeData';
import { useLabels } from '../../hooks';
import { useAnalyzeText, useFullAnalyze } from '../../api';
import { PageContext, OutersContainerContext, DeviceContext, UserContext, UserType, LanguageContext } from '../../contexts';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { normalizeTextForSearch, saveReturnTo } from '../../utils/helpers';
import { trackEvent } from '../../utils/analytics';
import styles from './Search.module.scss';

export const pageKey = "search";

const Search = () => {
  const searchInputRef = useRef();
  const { searchStr: rawSearchStr, wordEntry: rawWordEntry, wordId: wordIdParam } = useParams();
  const searchStr = useMemo(() => rawSearchStr ? normalizeTextForSearch(rawSearchStr) : rawSearchStr, [rawSearchStr]);
  const wordEntry = useMemo(() => rawWordEntry ? normalizeTextForSearch(rawWordEntry) : rawWordEntry, [rawWordEntry]);
  const wordId = useMemo(() => wordIdParam ? encodeURIComponent(wordIdParam) : wordIdParam, [wordIdParam]);
  const { loading: searchLoading, error: searchError, data: searchData } = useAnalyzeText(searchStr);
  const { loading: fullAnalyzeLoading, error: fullAnalyzeError, data: fullAnalyzeData } = useFullAnalyze(searchStr, wordEntry, wordId);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermissions, userType, dateUserExpired, pingUserData } = useContext(UserContext);
  const { language } = useContext(LanguageContext);
  const { setPageKey } = useContext(PageContext);
  const { isMobileView, isMobile } = useContext(DeviceContext);
  const {
    appLayoutTitleContainer,
    appLayoutSubTitleContainer,
    appLayoutHeaderSectionContainer,
    appLayoutStickySectionContainer,
  } = useContext(OutersContainerContext);
  const { getLabel, getLocalUrlByLanguage } = useLabels();
  const [translation, setTranslation] = useState(null)
  const [suppressBanner, setSuppressBanner] = useState(() => {
    try {
      const purchasedAt = sessionStorage.getItem('justPurchasedAt');
      return !!(purchasedAt && Date.now() - Number(purchasedAt) < 120000);
    } catch (_) { return false; }
  });

  useEffect(() => {
    try {
      const purchasedAt = sessionStorage.getItem('justPurchasedAt');
      if (!purchasedAt) return;
      const remaining = 120000 - (Date.now() - Number(purchasedAt));
      if (remaining <= 0) {
        sessionStorage.removeItem('justPurchasedAt');
        if (typeof pingUserData === 'function') pingUserData();
        setSuppressBanner(false);
        return;
      }
      const prefetchTimer = remaining > 30000
        ? setTimeout(() => { if (typeof pingUserData === 'function') pingUserData(); }, remaining - 30000)
        : null;
      const timer = setTimeout(() => {
        sessionStorage.removeItem('justPurchasedAt');
        setSuppressBanner(false);
      }, remaining);
      return () => {
        clearTimeout(timer);
        if (prefetchTimer) clearTimeout(prefetchTimer);
      };
    } catch (_) {}
  }, [pingUserData]);

  const isRenewalWindow = useMemo(() => {
    if (suppressBanner) return false;
    if (!dateUserExpired) {
      return false;
    }
    const expiry = new Date(dateUserExpired);
    if (Number.isNaN(expiry.getTime())) {
      return false;
    }
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    if (diffMs < 0) {
      return false;
    }
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= 14;
  }, [dateUserExpired, suppressBanner]);

  useEffect(() => {
    if (!hasPermissions) {
      if (userType === UserType.guest) {
        saveReturnTo(location.pathname + location.search);
      }
      navigate(getLocalUrlByLanguage(`/`));
    }
  }, [hasPermissions, userType, getLocalUrlByLanguage, navigate, location.pathname, location.search]);

  useEffect(() => {
    setPageKey(pageKey);
  }, [setPageKey]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.setValue(searchStr || '');
    }
  }, [searchStr]);

  // Focus search input when word selection changes
  useEffect(() => {
    if (searchInputRef.current && wordId) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [wordId]);

  // Reset translation when the search string changes or when no results are found
  useEffect(() => {
    if (!searchStr || (searchData && (searchData.resultType === 'Suggestions' || (searchData.words && searchData.words.length === 0)))) {
      setTranslation(null);
    }
  }, [searchStr, searchData]);

  // Handle navigation changes to ensure translation is reset properly
  useEffect(() => {
    if (isMobileView && searchStr && (!wordEntry || !wordId)) {
      setTranslation(null);
    }
  }, [isMobileView, searchStr, wordEntry, wordId]);

  useEffect(() => {
    let tempSearchStr = searchStr ? searchStr.replace(/^[\s\\?!]+|[\s\\?!]+$/g, '') : "";
    if ((tempSearchStr.startsWith('"') && tempSearchStr.endsWith('"')) ||
        (tempSearchStr.startsWith("'") && tempSearchStr.endsWith("'"))) {
      tempSearchStr = tempSearchStr.slice(1, -1);
    }

    if (searchStr && !isMobileView && !wordId && searchData && searchData.words && searchData.words.length > 0
      && tempSearchStr === searchData.query && !(searchData.words[0].id).startsWith('t')
      && !(searchData.words[0].id).startsWith('n')) {
      setTranslation(null);
      navigate(getLocalUrlByLanguage(`/search/${encodeURIComponent(tempSearchStr)}/${encodeURIComponent(searchData.words[0].inputWordEntry)}/${searchData.words[0].id}`));
    }
    else if (searchStr && !isMobileView && !wordId && searchData && searchData.words && searchData.words.length > 0
      && tempSearchStr === searchData.query) {
      setTranslation(searchData.words[0].translation);
      navigate(getLocalUrlByLanguage(`/search/${encodeURIComponent(tempSearchStr)}/${encodeURIComponent(searchData.words[0].inputWordEntry)}/${searchData.words[0].id}`));

    }
  }, [searchStr, wordId, isMobileView, searchData, getLocalUrlByLanguage, navigate]);


  const onSearchHandle = useCallback((value) => {
    if (value) {
      let normalized = normalizeTextForSearch(value);
      let cleaned = normalized.replace(/^[\s?!.,\\/]+|[\s?!.,\\/]+$/g, '');
      if ((cleaned.startsWith('"') && cleaned.endsWith('"')) ||
          (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
        cleaned = cleaned.slice(1, -1);
      }
      let tempSearchStr = cleaned.replace(/^\s+|\s+$/g, "");
      // Reset translation state when initiating a new search
      setTranslation(null);
      if (tempSearchStr) {
        trackEvent('dictionary_search', { search_term: tempSearchStr });
      }
      navigate(getLocalUrlByLanguage(`/search/${encodeURIComponent(tempSearchStr)}`));
    }
  }, [navigate, getLocalUrlByLanguage]);

  const onSearchButtonClickHandle = useCallback(() => {
    if (searchInputRef.current) {
      onSearchHandle(searchInputRef.current.getValue());
    }
  }, [onSearchHandle]);

  const onRenewButtonClickHandle = useCallback(() => {
    navigate(getLocalUrlByLanguage(`/purchase`));
  }, [getLocalUrlByLanguage, navigate]);

  const renewBannerAside = useMemo(() => {
    if (searchStr || isMobileView || !isRenewalWindow) {
      return null;
    }

    const container = appLayoutStickySectionContainer || appLayoutHeaderSectionContainer;
    if (!container) {
      return null;
    }

    const isHebrew = language === 'he';

    return (
      <OuterLayer
        container={container}
        TagWrapper='div'
        className={`${styles.renew_banner} ${isHebrew ? styles.renew_banner_right : styles.renew_banner_left}`}
      >
        <span className={styles.renew_banner_text}>{getLabel('search_renew_banner_text')}</span>
        <button
          type="button"
          className={styles.renew_banner_link}
          onClick={onRenewButtonClickHandle}
        >
          {getLabel('search_renew_banner_link')}
        </button>
      </OuterLayer>
    );
  }, [appLayoutHeaderSectionContainer, appLayoutStickySectionContainer, getLabel, isMobileView, isRenewalWindow, language, onRenewButtonClickHandle, searchStr]);

  const setSearchInputRef = useCallback((ref) => {
    searchInputRef.current = ref;
    if (searchInputRef.current) {
      searchInputRef.current.setValue(searchStr || '');
    }
  }, [searchStr]);

  const sliderItemBuilder = useCallback((item) => {
    if (!item) {
      return;
    }

    return <Href
      className={styles.search_results_word}
      to={getLocalUrlByLanguage(`/search/${encodeURIComponent(searchStr)}/${encodeURIComponent(item.inputWordEntry)}/${item.id}`)}
    >
      <div className={styles.search_results_word_title}>
        <h1>{item.inputWordEntry}</h1>
        <span className={styles.search_results_word_partOfSpeech}>{item.partOfSpeech}</span>
        {item.inputWordDiacritics && item.inputWordDiacritics !== item.inputWordEntry ? <span className={styles.search_results_word_diacritics}>{`(${item.inputWordDiacritics})`}</span> : ''}
      </div>
      <div className={styles.search_results_word_explain}><WordExplain explain={item.explain} linksEnable={false} /></div>
    </Href>;
  }, [getLocalUrlByLanguage, searchStr]);

  const selectedWord = useMemo(() => {
    let word = null, otherWords = [];
    if (searchData && searchData.words) {
      if (searchData.words.length == 1) {
        word = searchData.words[0];
      }
      else if (!wordId) {
        word = searchData.words[0];
        if (searchData.words.length > 1) {
          for (let i = 1; i < searchData.words.length; i++) {
            otherWords.push(searchData.words[i]);
          }
        }
      }
      else if (searchData.words.length > 1 && wordId) {
        for (let i = 0; i < searchData.words.length; i++) {
          if (searchData.words[i].id + '' === wordId + '') {
            word = searchData.words[i];
            if (searchData.words[i].id.startsWith('t') || searchData.words[i].id.startsWith('n'))
              setTranslation(searchData.words[i].translation)
          }
          else {
            otherWords.push(searchData.words[i]);
          }
        }
      }
    }
    return { word, otherWords };
  }, [wordId, searchData]);

  const searchInput = useMemo(() => {
    return <SearchInput onSearch={onSearchHandle} placeholder={getLabel("word_search_input_placeholder")} ref={setSearchInputRef} autoFocus={isMobileView ? false : true} />;
  }, [getLabel, onSearchHandle, setSearchInputRef, wordId]);

  const loader = useMemo(() => {
    return (searchLoading || fullAnalyzeLoading) && <Loader />;
  }, [searchLoading, fullAnalyzeLoading]);

  // Only show detailed results if we have a valid word entry and ID, or valid translation
  const shouldShowDetailedResults = (searchStr && wordEntry && wordId) || (searchStr && translation != null && !isMobileView);

  if (shouldShowDetailedResults) {
    return <>
      {isMobileView && <><OuterLayer container={appLayoutHeaderSectionContainer} TagWrapper='div' className={styles.header_search_input}>{searchInput}</OuterLayer>
        <div className={`${styles.container} ${styles.container_full_analyze}`}>
          <div className={styles.full_analyze_header}>
            <div className={styles.search_results_word} >
              {selectedWord.word && <div className={`${styles.search_results_selected_word} ${styles[`search_results_word_${selectedWord.word.type}`]}`}>
                <div className={styles.search_results_word_title}>
                  <h1>{selectedWord.word.inputWordEntry}</h1>
                  <span className={styles.search_results_word_partOfSpeech}>{selectedWord.word.partOfSpeech}</span>
                  {selectedWord.word.inputWordDiacritics && selectedWord.word.inputWordDiacritics !== selectedWord.word.inputWordEntry ? <span className={styles.search_results_word_diacritics}>{`(${selectedWord.word.inputWordDiacritics})`}</span> : ''}
                </div>
                <div className={styles.search_results_word_explain}><WordExplain explain={selectedWord.word.explain} /></div>
                <div className={styles.search_results_word_translation}>{selectedWord.word.translation}</div>
              </div>}
            </div>
          </div>
          <FullAnalyzeData data={fullAnalyzeData} searchStr={searchStr} translation={translation} />
          {selectedWord.otherWords.length > 0 && <div className={styles.full_analyze_other_words}>
            <div className={styles.full_analyze_other_words_title}>{getLabel('full_analyze_other_words_title')}</div>
            <Slider items={selectedWord.otherWords} itemBuilder={sliderItemBuilder} itemsInView={isMobileView ? 2 : 3} offset={isMobileView ? -0.5 : 0} />
          </div>}
        </div>
      </>}
      {!isMobileView && <div className={`${styles.container} ${styles.container_full_analyze}`}>
        <div className={styles.word_list_and_extension}>
          <div className={styles.search_and_words_list}>
            <div className={styles.search_input_aside} data-search_input_theme="full_analyze">{searchInput}</div>
            {selectedWord.word &&
              <div className={`${styles.search_results_selected_word} ${styles[`search_results_word_${selectedWord.word.type}`]}`}>
                <div className={styles.search_results_word_title}>
                  <h1>{selectedWord.word.inputWordEntry}</h1>
                  <span className={styles.search_results_word_partOfSpeech}>{selectedWord.word.partOfSpeech}</span>
                  {selectedWord.word.inputWordDiacritics && selectedWord.word.inputWordDiacritics !== selectedWord.word.inputWordEntry ?
                    <span className={styles.search_results_word_diacritics}>{`(${selectedWord.word.inputWordDiacritics})`}</span> : ''}
                </div>
                <div className={styles.search_results_word_explain}><WordExplain explain={selectedWord.word.explain} /></div>
                <div className={styles.search_results_word_translation}>{selectedWord.word.translation}</div>
              </div>}
            <div className={styles.others_words_list}>
              {selectedWord.otherWords.length > 0 && <div className={styles.full_analyze_other_words_overflow}><div className={styles.full_analyze_other_words}>
                {searchData && <SearchResults {...searchData} currentListWords={selectedWord.otherWords} />}
              </div>
              </div>}
            </div>
          </div>
          <FullAnalyzeData data={fullAnalyzeData} searchStr={searchStr} translation={translation} />
        </div>
      </div>}
      {loader}
    </>
  }

  if (searchStr && isMobileView || (searchData && searchData.resultType === 'Suggestions') || (searchData && searchData.words && searchData.words.length === 0)) {
    return <>
      {isMobileView && <OuterLayer container={appLayoutHeaderSectionContainer} TagWrapper='div' className={styles.header_search_input}>{searchInput}</OuterLayer>}
      {!isMobileView && <OuterLayer container={appLayoutTitleContainer} TagWrapper='span'>{getLabel("page_search_title")}</OuterLayer>}
      {!isMobileView && <OuterLayer container={appLayoutSubTitleContainer} dangerouslySetInnerHTML={{ __html: getLabel("page_search_sub_title") }} />}
      <div className={`${styles.container} ${styles.container_search_results} ${searchData && searchData.resultType ? styles['container_search_results_' + searchData.resultType] : ''}`} >
        {!isMobileView && <div className={styles.sticky_search_input}>{searchInput}</div>}
        {searchData && <SearchResults {...searchData} currentListWords={searchData.words} />}
      </div>
      {loader}
    </>;
  }
  {
    if (!searchStr)
      return <>
        <OuterLayer container={appLayoutTitleContainer} TagWrapper='span'>{getLabel("page_search_title")}</OuterLayer>
        <OuterLayer container={appLayoutSubTitleContainer} dangerouslySetInnerHTML={{ __html: getLabel("page_search_sub_title") }} />
        {renewBannerAside}
        <div className={styles.container} >
          {searchInput}
          <Button className={styles.search_but} type={buttonType.premium} onClick={onSearchButtonClickHandle} >{getLabel("search")}</Button>
        </div>
        {loader}
      </>;
  }
};

export default Search;
